// index.tsx - 设置页面（已拆分为基础设置、高级设置和级联设置三个标签页）
// 基础设置：包含基本信息、预览水印配置、Syslog上报配置
// 高级设置：包含扫描磁盘、数据同步、平台IP修改、安全APP设置等功能
// 级联设置：配置上级服务器IP地址，用于级联同步或上报

import React, { Children } from 'react';
import { List } from 'antd';
import { PlusOutlined, SafetyOutlined, ApiOutlined, LinkOutlined } from '@ant-design/icons';
import { Link, RouteComponentProps } from 'react-router-dom';
import './Index.less';
import HwzComponent from '../../common/component/HwzComponent';
import User from '../../common/model/user/User';
import Dept from '../../common/model/department/Dept';
import Moon from '../../common/model/global/Moon';
import axios from 'axios';
import {
  Button,
  Divider,
  message,
  Row,
  Col,
  Tag,
  Switch,
  Modal,
  Input,
  Tabs,
  Alert,
  Progress,
  Card,
  Space,
  Upload,
  Steps,
} from 'antd';
import {
  EditOutlined,
  ScanOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import InfoCell from '../widget/InfoCell';
import Preference from '../../common/model/preference/Preference';
import HwzContentCard from '../widget/HwzContentCard';
import HwzTitle from '../widget/HwzTitle';
import FileUtil from '../../common/util/FileUtil';
import ImagePreviewer from '../widget/previewer/ImagePreviewer';
import Lang from '../../common/model/global/Lang';
import SingleTextModal from '../widget/SingleTextModal';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import { ScanCronTypeList } from '../../common/model/preference/model/ScanCronType';
import {
  ScanScopeType,
  ScanScopeTypeMap,
} from '../../common/model/preference/model/ScanScopeType';
import PreviewEngineCell from './widget/PreviewEngineCell';
import PreviewEngine from '../../common/model/preference/model/PreviewEngine';

const { TabPane } = Tabs;

interface IProps extends RouteComponentProps {}

interface IState {
  syncTargetServer: string;
  userSyncing: boolean;
  deptSyncing: boolean;
  autoSyncNewFiles: boolean;
  syncOnlyApprovedFiles: boolean;
  // 平台IP地址相关
  platformIp: string;
  ipCidr: string;
  gateway: string;
  dns: string;
  ipUpdating: boolean;
  ipLoading: boolean;
  forceAutoUpload: boolean;
  appsecOnly: boolean;
  uploadDirs: string[];
  fileTypes: string[];
  securityAppLoading: boolean;
  securityAppSaving: boolean;
  // Syslog上报配置
  syslogEnable: boolean;
  syslogServer: string;
  syslogPort: number;
  syslogCron: string;
  syslogBatchSize: number;
  syslogLoading: boolean;
  syslogSaving: boolean;
  syslogResetting: boolean;  // 新增：重置按钮加载状态
  // 级联设置 - 上级服务器IP
  upstreamServer: string;
  upstreamLoading: boolean;
  upstreamSaving: boolean;
  // 系统升级相关
  frontendUploading: boolean;
  pcUploading: boolean;
  pcVersion: string;
  mobileUploading: boolean;
  // 整体升级相关
  backupRunning: boolean;
  backupRecords: Array<{ name: string; path: string; size: string; mtime: string }>;
  backupRecordsLoading: boolean;
  backupResultVisible: boolean;
  backupResult: { backupDir: string; output: string } | null;
  // 完整升级相关
  fullUpgradeUploading: boolean;
  fullUpgradeStarted: boolean;
  fullUpgradeRunning: boolean;
  fullUpgradeState: string;
  fullUpgradeMessage: string;
  fullUpgradeLog: string;
  fullUpgradeRestoreDir: string;
  upgradePollTimer: any;
  fullUpgradeResultVisible: boolean;
  fullUpgradeDone: boolean;
}

export default class Index extends HwzComponent<IProps, IState> {
  user: User = Moon.getSingleton().user;
  preference: Preference = Moon.getSingleton().preference;
  dept: Dept = new Dept();

  constructor(props: IProps) {
    super(props);

    this.state = {
      syncTargetServer: '',
      userSyncing: false,
      deptSyncing: false,
      autoSyncNewFiles: false,
      syncOnlyApprovedFiles: true,
      platformIp: '',
      ipCidr: '',
      gateway: '',
      dns: '8.8.8.8,114.114.114.114',
      ipUpdating: false,
      ipLoading: false,
      forceAutoUpload: false,
      appsecOnly: false,
      uploadDirs: [],
      fileTypes: [],
      securityAppLoading: false,
      securityAppSaving: false,
      syslogEnable: false,
      syslogServer: '',
      syslogPort: 514,
      syslogCron: '*/5 * * * *',
      syslogBatchSize: 1000,
      syslogLoading: false,
      syslogSaving: false,
      syslogResetting: false,  // 初始化
      // 级联设置初始值
      upstreamServer: '',
      upstreamLoading: false,
      upstreamSaving: false,
      // 系统升级初始值
      frontendUploading: false,
      pcUploading: false,
      pcVersion: '',
      mobileUploading: false,
      // 整体升级初始值
      backupRunning: false,
      backupRecords: [],
      backupRecordsLoading: false,
      backupResultVisible: false,
      backupResult: null,
      // 完整升级初始值
      fullUpgradeUploading: false,
      fullUpgradeStarted: false,
      fullUpgradeRunning: false,
      fullUpgradeState: 'idle',
      fullUpgradeMessage: '',
      fullUpgradeLog: '',
      fullUpgradeRestoreDir: '',
      upgradePollTimer: null,
      fullUpgradeResultVisible: false,
      fullUpgradeDone: false,
    };
  }

  componentDidMount() {
    this.refresh();
    this.fetchPlatformIp();
    this.fetchSecurityAppConfig();
    this.fetchSyslogConfig();
    this.fetchUpstreamConfig(); // 加载上级服务器配置
    this.fetchBackupRecords(); // 加载整体升级备份记录
    this.fetchUpgradeStatusOnce(); // 若存在进行中的升级任务则恢复轮询
  }

  componentWillUnmount() {
    this.stopUpgradePolling();
  }

  fetchSyslogConfig() {
    this.setState({ syslogLoading: true });
    fetch('/api/syslog/config', {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        // 兼容 code 为 200 或 'OK'
        if ((result.code === 200 || result.code === 'OK') && result.data) {
          this.setState({
            syslogEnable: result.data.syslog_enable === 'true',
            syslogServer: result.data.syslog_server || '',
            syslogPort: parseInt(result.data.syslog_port || '514', 10),
            syslogCron: result.data.syslog_cron || '*/5 * * * *',
            syslogBatchSize: parseInt(result.data.syslog_batch_size || '1000', 10),
          });
        } else {
          console.warn('获取 Syslog 配置失败，响应格式:', result);
        }
        this.setState({ syslogLoading: false });
      })
      .catch(err => {
        console.error('获取 Syslog 配置失败', err);
        this.setState({ syslogLoading: false });
      });
  }

  saveSyslogConfig() {
    const { syslogEnable, syslogServer, syslogPort, syslogCron, syslogBatchSize } = this.state;
    const payload = {
      syslog_enable: syslogEnable ? 'true' : 'false',
      syslog_server: syslogServer,
      syslog_port: syslogPort.toString(),
      syslog_cron: syslogCron,
      syslog_batch_size: syslogBatchSize.toString(),
    };
    this.setState({ syslogSaving: true });
    fetch('/api/syslog/config/save', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(result => {
        this.setState({ syslogSaving: false });
        if (result.code === 200 || result.code === 'OK') {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
        } else {
          MessageBoxUtil.error(result.message || Lang.t('operationFailed'));
        }
      })
      .catch(err => {
        console.error('保存 Syslog 配置失败', err);
        this.setState({ syslogSaving: false });
        MessageBoxUtil.error(Lang.t('operationFailed'));
      });
  }

  // 新增：重置上报时间点
  resetSyslogLastReported = () => {
    Modal.confirm({
      title: Lang.t('preference.resetSyslogConfirmTitle') || '重置上报时间点',
      content: Lang.t('preference.resetSyslogConfirmContent') || '重置后，转发器将从最近24小时的日志开始重新上报，可能导致重复数据。确定吗？',
      onOk: () => {
        this.setState({ syslogResetting: true });
        fetch('/api/syslog/reset-last-reported', {
          method: 'POST',
          credentials: 'include',
        })
          .then(res => res.json())
          .then(result => {
            this.setState({ syslogResetting: false });
            if (result.code === 200 || result.code === 'OK') {
              MessageBoxUtil.success(Lang.t('operationSuccess'));
              // 可选：重新加载配置
              this.fetchSyslogConfig();
            } else {
              MessageBoxUtil.error(result.message || Lang.t('operationFailed'));
            }
          })
          .catch(err => {
            console.error('重置失败', err);
            this.setState({ syslogResetting: false });
            MessageBoxUtil.error(Lang.t('operationFailed'));
          });
      },
    });
  };

  // ==================== 级联设置 - 上级服务器IP相关 ====================
  fetchUpstreamConfig() {
    this.setState({ upstreamLoading: true });
    fetch('/api/preference/upstream-config', {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 200 && result.data) {
          // 假设后端返回 { upstreamServer: "http://..." } 或空字符串
          this.setState({ upstreamServer: result.data.upstreamServer || '' });
        } else if (result.code === 200 && typeof result.data === 'string') {
          // 兼容直接返回字符串的情况
          this.setState({ upstreamServer: result.data || '' });
        }
        this.setState({ upstreamLoading: false });
      })
      .catch(err => {
        console.error('获取上级服务器配置失败', err);
        this.setState({ upstreamLoading: false });
      });
  }

  saveUpstreamConfig() {
    const { upstreamServer } = this.state;
    const payload = { upstreamServer: upstreamServer.trim() };
    this.setState({ upstreamSaving: true });
    fetch('/api/preference/edit/upstream-config', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(result => {
        this.setState({ upstreamSaving: false });
        if (result.code === 200 || result.code === 'OK') {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
        } else {
          MessageBoxUtil.error(result.message || Lang.t('operationFailed'));
        }
      })
      .catch(err => {
        console.error('保存上级服务器配置失败', err);
        this.setState({ upstreamSaving: false });
        MessageBoxUtil.error(Lang.t('operationFailed'));
      });
  }

  // ==================== 原有方法（保持不变） ====================
  addFileType = () => {
    const { fileTypes } = this.state;
    const newType = prompt(Lang.t('preference.enterFileType'), '.txt');
    if (newType && newType.trim()) {
      let ext = newType.trim();
      if (!ext.startsWith('.')) ext = '.' + ext;
      if (!fileTypes.includes(ext)) {
        this.setState({ fileTypes: [...fileTypes, ext] });
      } else {
        MessageBoxUtil.warning(Lang.t('preference.fileTypeExists'));
      }
    }
  };

  removeFileType = (index: number) => {
    const { fileTypes } = this.state;
    const newTypes = [...fileTypes];
    newTypes.splice(index, 1);
    this.setState({ fileTypes: newTypes });
  };

  fetchSecurityAppConfig() {
    this.setState({ securityAppLoading: true });
    fetch('/api/preference/security-app-config', {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        console.log('安全APP配置原始响应:', result);
        const isSuccess = result.code === 'OK' || result.code === 200;
        if (isSuccess && result.data) {
          let config = result.data;
          if (typeof config === 'string') config = JSON.parse(config);
          this.setState({
            forceAutoUpload: config.forceAutoUpload === true,
            appsecOnly: config.appsecOnly === true,
            uploadDirs: Array.isArray(config.uploadDirs) ? config.uploadDirs : [],
            fileTypes: Array.isArray(config.fileTypes) ? config.fileTypes : [],
          });
        } else {
          console.error('获取安全APP配置失败:', result);
        }
        this.setState({ securityAppLoading: false });
      })
      .catch(err => {
        console.error('获取安全APP配置失败', err);
        this.setState({ securityAppLoading: false });
      });
  }

  saveSecurityAppConfig() {
    const { forceAutoUpload, appsecOnly, uploadDirs, fileTypes } = this.state;
    const config = { forceAutoUpload, appsecOnly, uploadDirs, fileTypes };
    this.setState({ securityAppSaving: true });
    fetch('/api/preference/edit/security-app-config', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    })
      .then(res => res.json())
      .then(result => {
        this.setState({ securityAppSaving: false });
        if (result.code === 200 || result.code === 'OK') {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
        } else {
          MessageBoxUtil.error(result.message || Lang.t('operationFailed'));
        }
      })
      .catch(err => {
        console.error('保存安全APP配置失败', err);
        this.setState({ securityAppSaving: false });
        MessageBoxUtil.error(Lang.t('operationFailed'));
      });
  }

  addUploadDir() {
    const { uploadDirs } = this.state;
    const newDir = prompt(Lang.t('preference.enterDirectoryPath'));
    if (newDir && newDir.trim()) {
      this.setState({ uploadDirs: [...uploadDirs, newDir.trim()] });
    }
  }

  removeUploadDir(index: number) {
    const { uploadDirs } = this.state;
    const newDirs = [...uploadDirs];
    newDirs.splice(index, 1);
    this.setState({ uploadDirs: newDirs });
  }

  refresh() {
    const that = this;
    that.preference.httpFetch(function () {
      Promise.allSettled([
        new Promise((resolve, reject) => {
          that.preference.httpFetchWatermarkConfig(resolve, reject);
        }),
        new Promise((resolve, reject) => {
          that.preference.httpFetchFileSyncConfig(resolve, reject);
        }),
      ]).finally(() => {
        that.initializeFileSyncUI();
        that.updateUI();
      });
    });
  }

  fetchPlatformIp() {
    this.setState({ ipLoading: true });
    fetch('/api/preference/platform-ip', {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        console.log('平台IP接口返回完整数据:', result);
        let ip = result.data?.ip || '';
        console.log('提取的ip字段值:', ip);
        if (!ip && result.ip) ip = result.ip;
        console.log('最终使用的ip:', ip);
        this.setState({
          platformIp: ip,
          ipCidr: ip,
          ipLoading: false,
        });
        this.fetchNetworkConfig();
      })
      .catch(err => {
        console.error('获取平台IP失败:', err);
        this.setState({ ipLoading: false, platformIp: Lang.t('preference.fetchFailed') });
      });
  }

  fetchNetworkConfig() {
    fetch('/api/preference/network-config', {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 200 && result.data) {
          this.setState({
            gateway: result.data.gateway || '',
            dns: result.data.dns || '8.8.8.8,114.114.114.114',
          });
        }
      })
      .catch(err => console.error('获取网络配置失败', err));
  }

  updatePlatformIp() {
    const { ipCidr, gateway, dns } = this.state;
    console.log('=== updatePlatformIp 开始 ===', { ipCidr, gateway, dns });
    if (!ipCidr || ipCidr.trim() === '') {
      MessageBoxUtil.error(Lang.t('preference.pleaseEnterPlatformIp'));
      return;
    }
    let ip = ipCidr.trim();
    let netmask = '24';
    if (ip.includes('/')) {
      const parts = ip.split('/');
      ip = parts[0];
      netmask = parts[1];
    }
    console.log('解析后 - ip:', ip, 'netmask:', netmask);
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(ip)) {
      MessageBoxUtil.error(Lang.t('preference.invalidIpFormat'));
      return;
    }
    const maskNum = parseInt(netmask, 10);
    if (isNaN(maskNum) || maskNum < 1 || maskNum > 32) {
      MessageBoxUtil.error(Lang.t('preference.invalidNetmask'));
      return;
    }
    if (gateway && gateway.trim() !== '' && !ipRegex.test(gateway.trim())) {
      MessageBoxUtil.error(Lang.t('preference.invalidGateway'));
      return;
    }
    this.setState({ ipUpdating: true });
    const params = new URLSearchParams();
    params.append('ip', ip);
    params.append('netmask', netmask);
    params.append('gateway', gateway);
    params.append('dns', dns);
    console.log('发送请求参数:', { ip, netmask, gateway, dns });
    fetch('/api/preference/edit/platform-ip', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })
      .then(response => response.json())
      .then(result => {
        console.log('修改响应:', result);
        this.setState({ ipUpdating: false });
        const isSuccess = result.code === 'OK' || result.code === 200 || result.success === true;
        if (isSuccess) {
          const port = window.location.port || '8010';
          const newUrl = `http://${ip}:${port}/user/login`;
          MessageBoxUtil.success(Lang.t('preference.platformIpUpdated') + '，正在跳转到登录页...');
          setTimeout(() => {
            window.location.href = newUrl;
          }, 2000);
        } else {
          const errorMsg = result.message || Lang.t('operationFailed');
          MessageBoxUtil.error(errorMsg);
        }
      })
      .catch(err => {
        console.error('修改平台IP请求失败:', err);
        this.setState({ ipUpdating: false });
        MessageBoxUtil.error(Lang.t('preference.updateIpFailed'));
      });
  }

  cleanup() {
    const that = this;
    const preference = this.preference;
    SingleTextModal.open(
      Lang.t('preference.systemCleanupPrompt'),
      '',
      function (text: string) {
        preference.httpSystemCleanup(text, function () {
          MessageBoxUtil.success(Lang.t('operationSuccess'));
        });
      }
    );
  }

  handleSaveAutoSyncSettings = () => {
    const { autoSyncNewFiles, syncOnlyApprovedFiles, syncTargetServer } = this.state;
    const syncConfig = {
      autoSyncNewFiles,
      syncOnlyApprovedFiles,
      syncTargetServer,
    };
    this.preference.updateSyncConfig(
      syncConfig,
      (response) => {
        this.preference.filesSyncConfig = JSON.stringify(syncConfig);
        MessageBoxUtil.success(Lang.t('operationSuccess'));
      },
      (error) => {
        MessageBoxUtil.error(Lang.t('operationFailed'));
      }
    );
  };

  handleAutoSyncNewFilesChange = (checked: boolean) => {
    this.setState({ autoSyncNewFiles: checked });
  };

  immediateScan() {
    const hide = message.loading(Lang.t('preference.scanStarting'), 0);
    this.preference.httpStartScan(
      (response: any) => {
        hide();
        console.log('Scan start response:', JSON.stringify(response, null, 2));
        const taskId = response?.data?.data?.taskId;
        if (!taskId) {
          message.error(Lang.t('preference.scanStartFailed'));
          return;
        }
        this.showScanProgress(taskId);
      },
      (error: any) => {
        hide();
        console.error('Scan start error:', error);
        MessageBoxUtil.error(Lang.t('preference.scanStartFailed') + (error.message || ''));
      }
    );
  }

  showScanProgress(taskId: string) {
    let modal = Modal.info({
      title: Lang.t('preference.scanProgress'),
      content: (
        <div>
          <p>{Lang.t('preference.scanning')}...</p>
          <Progress percent={0} status="active" />
          <p className="text-muted">{Lang.t('preference.currentFile')}: </p>
        </div>
      ),
      okText: Lang.t('close'),
      okButtonProps: { disabled: true },
      maskClosable: false,
      width: 500,
    });
    let interval: NodeJS.Timeout | null = null;
    let isMounted = true;
    const pollProgress = () => {
      this.preference.httpGetScanProgress(
        taskId,
        (response: any) => {
          if (!isMounted) return;
          const status = response?.data?.data;
          if (!status) {
            clearInterval(interval!);
            modal.destroy();
            message.error(Lang.t('preference.scanProgressFailed'));
            return;
          }
          if (!status.running) {
            clearInterval(interval!);
            modal.update({
              title: status.error ? Lang.t('preference.scanFailed') : Lang.t('preference.scanComplete'),
              content: (
                <div>
                  <p>{Lang.t('preference.totalFiles')}: {status.total || 0}</p>
                  <p>{Lang.t('preference.processedFiles')}: {status.processed || 0}</p>
                  {status.error && (
                    <Alert type="error" message={Lang.t('preference.scanError')} description={status.error} showIcon />
                  )}
                </div>
              ),
              okButtonProps: { disabled: false },
            });
          } else {
            const percent = status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;
            modal.update({
              content: (
                <div>
                  <Progress percent={percent} status="active" />
                  <p>{Lang.t('preference.processedFiles')}: {status.processed || 0} / {status.total || '?'}</p>
                  <p className="text-muted" style={{ wordBreak: 'break-all' }}>
                    {Lang.t('preference.currentFile')}: {status.current || ''}
                  </p>
                </div>
              ),
            });
          }
        },
        (error: any) => {
          console.error('轮询进度失败', error);
        }
      );
    };
    pollProgress();
    interval = setInterval(pollProgress, 1000);
    const cleanup = () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      if (modal) {
        modal.destroy();
      }
    };
    const originalOnCancel = modal.update;
    modal.update = (config: any) => {
      if (config.visible === false) {
        cleanup();
      }
      return originalOnCancel.call(modal, config);
    };
  }

  previewWatermark = () => {
    const { watermarkConfig } = this.preference;
    const previewContent = (
      <div
        style={{
          position: 'relative',
          width: '500px',
          height: '400px',
          border: '1px solid #d9d9d9',
          background: 'white',
          margin: '0 auto',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            background: `repeating-linear-gradient(
              ${watermarkConfig.rotate}deg,
              transparent,
              transparent 100px,
              ${watermarkConfig.color.replace(/[\d.]+\)$/, '0.1)')} 100px,
              ${watermarkConfig.color.replace(/[\d.]+\)$/, '0.1)')} 200px
            )`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) rotate(${watermarkConfig.rotate}deg)`,
            fontSize: `${watermarkConfig.fontSize * 1.5}px`,
            color: watermarkConfig.color,
            opacity: watermarkConfig.opacity,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            fontWeight: 'bold',
          }}
        >
          {watermarkConfig.text}
        </div>
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            color: '#666',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h3>{Lang.t('preference.watermarkPreview')}</h3>
          <p>{Lang.t('preference.watermarkPreviewContent')}</p>
          <p>{Lang.t('preference.watermarkText')}: <strong>{watermarkConfig.text}</strong></p>
          <p>{Lang.t('preference.watermarkFontSize')}: <strong>{watermarkConfig.fontSize}px</strong></p>
          <p>{Lang.t('preference.watermarkOpacity')}: <strong>{watermarkConfig.opacity}</strong></p>
          <p>{Lang.t('preference.watermarkRotate')}: <strong>{watermarkConfig.rotate}°</strong></p>
        </div>
      </div>
    );
    Modal.info({
      title: Lang.t('preference.watermarkPreview'),
      width: 600,
      content: previewContent,
      icon: null,
      okText: Lang.t('close'),
    });
  };

  handleTargetServerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ syncTargetServer: e.target.value });
  };

  initializeFileSyncUI() {
    const { filesSyncConfig } = this.preference;
    let config: Record<string, any> = {};
    if (filesSyncConfig) {
      try {
        config = JSON.parse(filesSyncConfig);
      } catch (e) {
        console.error('文件同步配置解析失败:', e);
      }
    }
    this.setState({
      autoSyncNewFiles: !!config.autoSyncNewFiles,
      syncOnlyApprovedFiles: config.syncOnlyApprovedFiles !== false,
      syncTargetServer: config.syncTargetServer || '',
    });
  }

  syncUsers = () => {
    const { syncTargetServer } = this.state;
    if (!syncTargetServer) {
      MessageBoxUtil.error(Lang.t('preference.enterServerAddress'));
      return;
    }
    if (!syncTargetServer.startsWith('http://') && !syncTargetServer.startsWith('https://')) {
      MessageBoxUtil.error(Lang.t('preference.invalidServerAddressFormat'));
      return;
    }
    this.setState({ userSyncing: true });
    this.user.httpTriggerSync(
      syncTargetServer,
      (response: any) => {
        this.setState({ userSyncing: false });
        MessageBoxUtil.success(Lang.t('preference.userSyncCompleted'));
      },
      (error: any) => {
        this.setState({ userSyncing: false });
        const errorMessage = Lang.t('preference.syncFailed') + (error.message || Lang.t('common.unknownError'));
        MessageBoxUtil.error(errorMessage);
      }
    );
  };

  syncDepartments = () => {
    const { syncTargetServer } = this.state;
    if (!syncTargetServer) {
      MessageBoxUtil.error(Lang.t('preference.enterServerAddress'));
      return;
    }
    if (!syncTargetServer.startsWith('http://') && !syncTargetServer.startsWith('https://')) {
      MessageBoxUtil.error(Lang.t('preference.invalidServerAddressFormat'));
      return;
    }
    Modal.confirm({
      title: Lang.t('preference.deptSyncConfirm'),
      content: Lang.t('preference.deptSyncConfirmContent'),
      onOk: () => {
        this.setState({ deptSyncing: true });
        this.dept.httpTriggerSync(
          syncTargetServer,
          (response: any) => {
            console.log('部门同步成功回调，响应数据:', response);
            this.setState({ deptSyncing: false });
            if (response.successCount !== undefined) {
              MessageBoxUtil.success(Lang.t('preference.deptSyncCompleted', response.successCount.toString(), response.total?.toString() || '0'));
            } else if (response.data?.successCount !== undefined) {
              MessageBoxUtil.success(Lang.t('preference.deptSyncCompleted', response.data.successCount.toString(), response.data.total?.toString() || '0'));
            } else {
              MessageBoxUtil.success(Lang.t('preference.deptSyncCompletedGeneric'));
              console.log('部门同步成功，但返回数据格式不标准:', response);
            }
          },
          (error: any) => {
            console.error('部门同步失败回调:', error);
            this.setState({ deptSyncing: false });
            const errorMessage = Lang.t('preference.deptSyncFailed') + (error.message || Lang.t('common.unknownError'));
            MessageBoxUtil.error(errorMessage);
          }
        );
      },
    });
  };

  getDbTypeLabel() {
    const type = (this.preference.dbType || '').toLowerCase();
    if (type === 'sqlite') {
      return 'SQLite';
    }
    if (type === 'mysql') {
      return 'MySQL';
    }
    return this.preference.dbType || 'N/A';
  }

  renderBasicSettings() {
    const {
      previewConfig: { previewEngines },
      watermarkConfig,
    } = this.preference;
    const { syslogEnable, syslogServer, syslogPort, syslogCron, syslogBatchSize, syslogLoading, syslogSaving, syslogResetting } = this.state;
    return (
      <>
        <Row gutter={[24, 24]}>
          {/* 左侧列 - 基本设置 */}
          <Col xs={24} md={12}>
            <div className="basic-info">
              <header>
                <p className="title">{Lang.t('preference.basic')}</p>
                <Link to={'/preference/edit'}>
                  <Button type={'primary'} icon={<EditOutlined />}>{Lang.t('edit')}</Button>
                </Link>
              </header>
              <InfoCell name={Lang.t('preference.websiteName')}>{this.preference.name}</InfoCell>
              <InfoCell name={Lang.t('preference.logo')}>
                {this.preference.logoUrl && (
                  <img src={this.preference.logoUrl} alt="logo" className="img-logo" onClick={() => ImagePreviewer.showSinglePhoto(this.preference.logoUrl!)} />
                )}
              </InfoCell>
              <InfoCell name={'icon'}>
                {this.preference.faviconUrl && (
                  <img src={this.preference.faviconUrl} alt="logo" className="img-favicon" onClick={() => ImagePreviewer.showSinglePhoto(this.preference.faviconUrl!)} />
                )}
              </InfoCell>
              <InfoCell name={Lang.t('preference.zipMaxNumLimit')}>
                {this.preference.downloadDirMaxNum === -1 ? Lang.t('preference.noLimit') : this.preference.downloadDirMaxNum}
              </InfoCell>
              <InfoCell name={Lang.t('preference.zipMaxSizeLimit')}>{FileUtil.humanFileSize(this.preference.downloadDirMaxSize)}</InfoCell>
              <InfoCell name={Lang.t('preference.userDefaultSizeLimit')}>{FileUtil.humanFileSize(this.preference.defaultTotalSizeLimit)}</InfoCell>
              <InfoCell name={Lang.t('preference.matterBinDefaultSaveDay')}>{this.preference.deletedKeepDays} {Lang.t('day')}</InfoCell>
              <InfoCell name={Lang.t('preference.allowRegister')}>{this.preference.allowRegister ? Lang.t('yes') : Lang.t('no')}</InfoCell>
              <InfoCell name={Lang.t('preference.antivirusEnabled')}>{this.preference.antivirusEnabled ? Lang.t('yes') : Lang.t('no')}</InfoCell>
              {this.preference.antivirusEnabled && (
                <InfoCell name={Lang.t('preference.virusScanTimeout')}>{this.preference.virusScanTimeoutSeconds} {Lang.t('preference.second', '秒')}</InfoCell>
              )}
              <InfoCell name={Lang.t('preference.fullTextSearchEnabled')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{this.preference.fullTextSearchEnabled ? Lang.t('yes') : Lang.t('no')}</span>
                  {this.preference.fullTextSearchEnabled && this.preference.forbiddenKeywords && (
                    <>
                      <span style={{ color: '#ddd' }}>|</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {this.preference.forbiddenKeywords.split(',').filter(keyword => keyword.trim()).slice(0, 5).map(keyword => (
                          <Tag key={keyword} className="mb5">{keyword.trim()}</Tag>
                        ))}
                        {this.preference.forbiddenKeywords.split(',').filter(keyword => keyword.trim()).length > 5 && (
                          <Tag>+{this.preference.forbiddenKeywords.split(',').filter(keyword => keyword.trim()).length - 5} more</Tag>
                        )}
                        {this.preference.forbiddenKeywords.split(',').filter(keyword => keyword.trim()).length === 0 && (
                          <span className="text-muted">{Lang.t('preference.noForbiddenKeywords')}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </InfoCell>
              <InfoCell name={Lang.t('preference.forbiddenExtensionsEnabled')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span>{this.preference.forbiddenExtensionsEnabled ? Lang.t('yes') : Lang.t('no')}</span>
                  {this.preference.forbiddenExtensionsEnabled && this.preference.forbiddenExtensions && (
                    <>
                      <span style={{ color: '#ddd' }}>|</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {this.preference.forbiddenExtensions.split(',').filter(ext => ext.trim()).slice(0, 5).map(ext => (
                          <Tag key={ext} className="mb5">.{ext.trim()}</Tag>
                        ))}
                        {this.preference.forbiddenExtensions.split(',').filter(ext => ext.trim()).length > 5 && (
                          <Tag>+{this.preference.forbiddenExtensions.split(',').filter(ext => ext.trim()).length - 5} more</Tag>
                        )}
                        {this.preference.forbiddenExtensions.split(',').filter(ext => ext.trim()).length === 0 && (
                          <span className="text-muted">{Lang.t('preference.noForbiddenExtensions')}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </InfoCell>
              <InfoCell name={Lang.t('preference.version')}>
                <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '14px' }}>{this.preference.version || 'N/A'}</span>
              </InfoCell>
              <InfoCell name={Lang.t('preference.dbType')}>
                <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '14px' }}>{this.getDbTypeLabel()}</span>
              </InfoCell>
            </div>
          </Col>

          {/* 右侧列 - 预览与水印 */}
          <Col xs={24} md={12}>
            <div className="preview-info">
              <header>
                <p className="title">{Lang.t('preference.preview')}</p>
                <Link to={'/preference/engine/edit'}>
                  <Button type={'primary'} icon={<EditOutlined />}>{Lang.t('edit')}</Button>
                </Link>
              </header>
              <div className="content">
                {previewEngines.map((engine, index) => (
                  <PreviewEngineCell key={index} engine={engine} order={index + 1} />
                ))}
                {Children.toArray(PreviewEngine.defaultPreviewEngines().map((engine) => (
                  <PreviewEngineCell engine={engine} />
                )))}
              </div>
              <Divider />
              <div className="watermark-info">
                <header>
                  <p className="title">{Lang.t('preference.watermarkSet')}</p>
                  <div>
                    <Button type="default" className="mr10" icon={<EyeOutlined />} onClick={this.previewWatermark} disabled={!watermarkConfig.enabled}>
                      {Lang.t('preference.preview')}
                    </Button>
                    <Link to={'/preference/edit'}>
                      <Button type={'primary'} icon={<EditOutlined />}>{Lang.t('edit')}</Button>
                    </Link>
                  </div>
                </header>
                <InfoCell name={Lang.t('preference.watermarkEnabled')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch checked={watermarkConfig.enabled} size="small" disabled />
                    <span>{watermarkConfig.enabled ? Lang.t('preference.enabled') : Lang.t('preference.disabled')}</span>
                  </div>
                </InfoCell>
                {watermarkConfig.enabled && (
                  <>
                    <InfoCell name={Lang.t('preference.watermarkText')}>{watermarkConfig.text}</InfoCell>
                    <InfoCell name={Lang.t('preference.watermarkFontSize')}>{watermarkConfig.fontSize}px</InfoCell>
                    <InfoCell name={Lang.t('preference.watermarkOpacity')}>{watermarkConfig.opacity}</InfoCell>
                    <InfoCell name={Lang.t('preference.watermarkRotate')}>{watermarkConfig.rotate}°</InfoCell>
                    <InfoCell name={Lang.t('preference.watermarkColor')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '16px', backgroundColor: watermarkConfig.color, border: '1px solid #d9d9d9', borderRadius: '2px' }} />
                        <span>{watermarkConfig.color}</span>
                      </div>
                    </InfoCell>
                    <InfoCell name={Lang.t('preference.showUsername')}>{watermarkConfig.showUser ? Lang.t('yes') : Lang.t('no')}</InfoCell>
                    <InfoCell name={Lang.t('preference.pdfWatermarkText')}>{watermarkConfig.pdfText}</InfoCell>
                    <InfoCell name={Lang.t('preference.imageWatermarkText')}>{watermarkConfig.imageText}</InfoCell>
                    <InfoCell name={Lang.t('preference.documentWatermarkText')}>{watermarkConfig.documentText}</InfoCell>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Syslog 服务器配置模块 - 增加重置按钮 */}
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <ApiOutlined />
                  <span>{Lang.t('preference.syslogSettings')}</span>
                </Space>
              }
              loading={syslogLoading}
              extra={
                <Space>
                  <Button
                    danger
                    size="small"
                    loading={syslogResetting}
                    onClick={this.resetSyslogLastReported}
                  >
                    {Lang.t('preference.resetSyslogTime') || '重置上报时间'}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    loading={syslogSaving}
                    onClick={() => this.saveSyslogConfig()}
                  >
                    {Lang.t('save')}
                  </Button>
                </Space>
              }
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <span>{Lang.t('preference.syslogEnable')}：</span>
                  <Switch checked={syslogEnable} onChange={(checked) => this.setState({ syslogEnable: checked })} />
                </Col>
                <Col xs={24} sm={16}>
                  <span>{Lang.t('preference.syslogServer')}：</span>
                  <Input
                    placeholder={Lang.t('preference.syslogServerPlaceholder')}
                    value={syslogServer}
                    onChange={(e) => this.setState({ syslogServer: e.target.value })}
                    style={{ width: '100%', maxWidth: 300 }}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={8}>
                  <span>{Lang.t('preference.syslogPort')}：</span>
                  <Input
                    type="number"
                    value={syslogPort}
                    onChange={(e) => this.setState({ syslogPort: parseInt(e.target.value, 10) || 514 })}
                    style={{ width: 120 }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <span>{Lang.t('preference.syslogCron')}：</span>
                  <Input
                    placeholder={Lang.t('preference.syslogCronPlaceholder')}
                    value={syslogCron}
                    onChange={(e) => this.setState({ syslogCron: e.target.value })}
                    style={{ width: 200 }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <span>{Lang.t('preference.syslogBatchSize')}：</span>
                  <Input
                    type="number"
                    value={syslogBatchSize}
                    onChange={(e) => this.setState({ syslogBatchSize: parseInt(e.target.value, 10) || 1000 })}
                    style={{ width: 120 }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                {Lang.t('preference.syslogDescription')}
              </div>
            </Card>
          </Col>
        </Row>
      </>
    );
  }

  renderAdvancedSettings() {
    const {
      scanConfig,
      ctrlMod,
      syncSet,
    } = this.preference;
    const {
      syncTargetServer,
      userSyncing,
      deptSyncing,
      autoSyncNewFiles,
      syncOnlyApprovedFiles,
      platformIp,
      ipCidr,
      gateway,
      dns,
      ipUpdating,
      ipLoading,
      forceAutoUpload,
      appsecOnly,
      uploadDirs,
      fileTypes,
      securityAppLoading,
      securityAppSaving,
    } = this.state;
    const showScanSync = ctrlMod !== 3;
    return (
      <div>
        {/* 平台IP 与 安全APP 并列行 */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {/* 左侧：平台 IP 地址修改模块 */}
          <Col xs={24} md={12}>
            <Card
              title={
                <Space>
                  <ApiOutlined />
                  <span>{Lang.t('preference.platformIpSettings')}</span>
                </Space>
              }
            >
              <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8} md={6}>
                  <span>{Lang.t('preference.currentPlatformIp')}：</span>
                </Col>
                <Col xs={24} sm={16} md={18}>
                  {ipLoading ? <span>{Lang.t('preference.loading')}</span> : <Tag color="blue">{platformIp || Lang.t('preference.notConfigured')}</Tag>}
                </Col>
              </Row>
              <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8} md={6}>
                  <span>{Lang.t('preference.ipWithNetmask')}：</span>
                </Col>
                <Col xs={24} sm={16} md={12}>
                  <Input
                    placeholder={Lang.t('preference.ipWithNetmaskPlaceholder')}
                    value={ipCidr}
                    onChange={(e) => this.setState({ ipCidr: e.target.value })}
                  />
                </Col>
                <Col xs={24} sm={0} md={6} />
              </Row>
              <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8} md={6}>
                  <span>{Lang.t('preference.gateway')}：</span>
                </Col>
                <Col xs={24} sm={16} md={12}>
                  <Input
                    placeholder={Lang.t('preference.gatewayPlaceholder')}
                    value={gateway}
                    onChange={(e) => this.setState({ gateway: e.target.value })}
                  />
                </Col>
                <Col xs={24} sm={0} md={6} />
              </Row>
              <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8} md={6}>
                  <span>{Lang.t('preference.dns')}：</span>
                </Col>
                <Col xs={24} sm={16} md={12}>
                  <Input
                    placeholder={Lang.t('preference.dnsPlaceholder')}
                    value={dns}
                    onChange={(e) => this.setState({ dns: e.target.value })}
                  />
                </Col>
                <Col xs={24} sm={0} md={6} />
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8} md={6} />
                <Col xs={24} sm={16} md={12}>
                  <Button type="primary" onClick={() => this.updatePlatformIp()} loading={ipUpdating}>
                    {Lang.t('preference.saveIp')}
                  </Button>
                </Col>
              </Row>
              <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>{Lang.t('preference.platformIpTips')}</div>
            </Card>
          </Col>

          {/* 右侧：安全APP相关设置模块 */}
          <Col xs={24} md={12}>
            <Card
              title={
                <Space>
                  <SafetyOutlined />
                  <span>{Lang.t('preference.securityAppSettings')}</span>
                </Space>
              }
              loading={securityAppLoading}
              extra={
                <Button type="primary" size="small" loading={securityAppSaving} onClick={() => this.saveSecurityAppConfig()}>
                  {Lang.t('save')}
                </Button>
              }
            >
              {/* 强制自动上传开关 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Switch checked={forceAutoUpload} onChange={(checked) => this.setState({ forceAutoUpload: checked })} />
                  <div>
                    <div>{Lang.t('preference.forceAutoUpload')}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{Lang.t('preference.forceAutoUploadDesc')}</div>
                  </div>
                </div>
              </div>
              {/* 仅允许加密环境APP登录开关 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Switch checked={appsecOnly} onChange={(checked) => this.setState({ appsecOnly: checked })} />
                  <div>
                    <div>{Lang.t('preference.appsecOnly')}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{Lang.t('preference.appsecOnlyDesc')}</div>
                  </div>
                </div>
              </div>
              <Divider />
              {/* 上传目录列表 */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span>{Lang.t('preference.uploadDirectories')}</span>
                  <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => this.addUploadDir()}>
                    {Lang.t('preference.addDirectory')}
                  </Button>
                </div>
                {uploadDirs.length === 0 ? (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>{Lang.t('preference.noUploadDirectories')}</div>
                ) : (
                  <List
                    size="small"
                    bordered
                    dataSource={uploadDirs}
                    renderItem={(dir: string, idx: number) => (
                      <List.Item
                        actions={[
                          <Button type="link" danger size="small" onClick={() => this.removeUploadDir(idx)}>
                            {Lang.t('delete')}
                          </Button>
                        ]}
                      >
                        {dir}
                      </List.Item>
                    )}
                  />
                )}
              </div>
              {/* 文件类型列表 */}
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span>{Lang.t('preference.fileTypes')}</span>
                  <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => this.addFileType()}>
                    {Lang.t('preference.addFileType')}
                  </Button>
                </div>
                {fileTypes.length === 0 ? (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>{Lang.t('preference.noFileTypes')}</div>
                ) : (
                  <List
                    size="small"
                    bordered
                    dataSource={fileTypes}
                    renderItem={(type: string, idx: number) => (
                      <List.Item
                        actions={[
                          <Button type="link" danger size="small" onClick={() => this.removeFileType(idx)}>
                            {Lang.t('delete')}
                          </Button>
                        ]}
                      >
                        {type}
                      </List.Item>
                    )}
                  />
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* 扫描 & 同步设置区域 */}
        {showScanSync ? (
          <Row gutter={[24, 24]}>
            {/* 扫描设置列 */}
            <Col xs={24} md={syncSet === 0 ? 24 : 12}>
              <div className="scan-info">
                <header>
                  <p className="title">{Lang.t('preference.scan')}</p>
                  <div>
                    <Button type="primary" className="mr10" icon={<ScanOutlined />} onClick={() => this.immediateScan()}>
                      {Lang.t('preference.scanImmediately')}
                    </Button>
                    <Link to={'/preference/scan/edit'}>
                      <Button type={'primary'} icon={<EditOutlined />}>{Lang.t('preference.scanCron')}</Button>
                    </Link>
                  </div>
                </header>
                <div className="content">
                  <InfoCell name={Lang.t('preference.enableScan')}>
                    {scanConfig.enable ? Lang.t('preference.enabled') : Lang.t('preference.disabled')}
                  </InfoCell>
                  {scanConfig.enable ? (
                    <>
                      <InfoCell name={Lang.t('preference.scanCron')}>
                        {ScanCronTypeList.find((option: any) => option.value === scanConfig.cron)?.name || scanConfig.cron}
                      </InfoCell>
                      <InfoCell name={Lang.t('preference.scanScope')}>
                        {ScanScopeTypeMap[scanConfig.scope as keyof typeof ScanScopeTypeMap].name}
                      </InfoCell>
                      {scanConfig.scope === ScanScopeType.CUSTOM && (
                        <InfoCell name={Lang.t('preference.scanSpaces')}>{scanConfig.spaceNames.join(', ')}</InfoCell>
                      )}
                      <InfoCell name={Lang.t('preference.autoClean')}>
                        {scanConfig.autoCleanEnabled ? Lang.t('preference.enabled') : Lang.t('preference.disabled')}
                      </InfoCell>
                      {scanConfig.autoCleanEnabled && (
                        <InfoCell name={Lang.t('preference.cleanBeforeDays')}>{scanConfig.autoCleanDays} {Lang.t('preference.day')}</InfoCell>
                      )}
                    </>
                  ) : (
                    <InfoCell name={Lang.t('preference.scanStatus')}>{Lang.t('preference.scanDisabledHint')}</InfoCell>
                  )}
                </div>
              </div>
            </Col>

            {/* 同步设置列 */}
            {syncSet !== 0 && (
              <Col xs={24} md={12}>
                <div className="sync-info">
                  <header>
                    <p className="title">{Lang.t('preference.SyncSet')}</p>
                  </header>
                  <div className="sync-server-address-section" style={{ marginBottom: '20px' }}>
                    <Alert message={Lang.t('preference.syncGeneralTips')} type="info" showIcon style={{ marginBottom: 16 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', whiteSpace: 'nowrap', lineHeight: '32px' }}>{Lang.t('preference.targetServerAddress')}</span>
                      <Input
                        placeholder={Lang.t('preference.targetServerAddressPlaceholder')}
                        value={syncTargetServer}
                        onChange={this.handleTargetServerChange}
                        style={{ width: '240px', flex: 1, minWidth: '180px' }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>{Lang.t('preference.syncServerDescription')}</div>
                  </div>
                  <Tabs defaultActiveKey="users" className="sync-tabs">
                    <TabPane tab={<span><UserOutlined />{Lang.t('preference.userSync')}</span>} key="users">
                      <div className="sync-tab-content">
                        <Alert message={Lang.t('preference.userSyncTips')} type="info" showIcon style={{ marginBottom: 16 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: 16 }}>
                          <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            loading={userSyncing}
                            onClick={this.syncUsers}
                            style={{ width: '160px' }}
                            disabled={!syncTargetServer}
                          >
                            {userSyncing ? Lang.t('preference.syncing') : Lang.t('preference.syncAllUsers')}
                          </Button>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>{Lang.t('preference.userSyncDescription')}</div>
                      </div>
                    </TabPane>
                    <TabPane tab={<span><TeamOutlined />{Lang.t('preference.deptSync')}</span>} key="departments">
                      <div className="sync-tab-content">
                        <Alert message={Lang.t('preference.deptSyncTips')} type="info" showIcon style={{ marginBottom: 16 }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: 16 }}>
                          <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            loading={deptSyncing}
                            onClick={this.syncDepartments}
                            style={{ width: '160px' }}
                            disabled={!syncTargetServer}
                          >
                            {deptSyncing ? Lang.t('preference.syncing') : Lang.t('preference.syncAllDepts')}
                          </Button>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>{Lang.t('preference.deptSyncDescription')}</div>
                      </div>
                    </TabPane>
                    <TabPane tab={<span><SyncOutlined />{Lang.t('preference.autoSync')}</span>} key="auto">
                      <div className="sync-tab-content">
                        <div style={{ marginTop: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                            <Switch checked={autoSyncNewFiles} onChange={this.handleAutoSyncNewFilesChange} style={{ marginTop: '4px' }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', lineHeight: '22px' }}>{Lang.t('preference.autoSyncNewFilesHint')}</div>
                              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{Lang.t('preference.autoSyncNewFilesDescription')}</div>
                            </div>
                            <Button type="primary" onClick={this.handleSaveAutoSyncSettings} style={{ width: '120px' }} disabled={!syncTargetServer}>
                              {Lang.t('preference.saveSettings')}
                            </Button>
                          </div>
                          {autoSyncNewFiles && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginLeft: '34px', marginTop: '8px' }}>
                              <Switch
                                checked={syncOnlyApprovedFiles}
                                onChange={(checked) => this.setState({ syncOnlyApprovedFiles: checked })}
                                style={{ marginTop: '4px' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', lineHeight: '22px' }}>{Lang.t('preference.syncOnlyApprovedFilesHint')}</div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{Lang.t('preference.syncOnlyApprovedFilesDescription')}</div>
                              </div>
                              <div style={{ width: '120px' }}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabPane>
                  </Tabs>
                </div>
              </Col>
            )}
          </Row>
        ) : (
          <Alert message={Lang.t('preference.advancedSettingsDisabled')} description={Lang.t('preference.advancedSettingsDisabledDesc')} type="warning" showIcon />
        )}
      </div>
    );
  }

  // 新增：级联设置页面
  renderCascadeSettings() {
    const { upstreamServer, upstreamLoading, upstreamSaving } = this.state;
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <LinkOutlined />
                <span>{Lang.t('preference.cascadeSettings')}</span>
              </Space>
            }
            loading={upstreamLoading}
            extra={
              <Button type="primary" loading={upstreamSaving} onClick={() => this.saveUpstreamConfig()}>
                {Lang.t('save')}
              </Button>
            }
          >
            <Alert
              message={Lang.t('preference.cascadeSettingsDesc')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={6} md={4}>
                <span>{Lang.t('preference.upstreamServer')}：</span>
              </Col>
              <Col xs={24} sm={18} md={16}>
                <Input
                  placeholder={Lang.t('preference.upstreamServerPlaceholder')}
                  value={upstreamServer}
                  onChange={(e) => this.setState({ upstreamServer: e.target.value })}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
              {Lang.t('preference.upstreamServerTips')}
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  // ==================== 新增：系统升级相关方法 ====================

  handleUploadFrontend = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      message.error(Lang.t('preference.invalidFrontendPackage'));
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);
    this.setState({ frontendUploading: true });
    this.preference.httpUpload(
      Preference.URL_API_UPGRADE_FRONTEND,
      formData,
      () => {
        message.success(Lang.t('preference.upgradeFrontendSuccess'));
        this.setState({ frontendUploading: false });
      },
      (err: any) => {
        console.error('上传前端升级包失败:', err);
        message.error(Lang.t('preference.upgradeFrontendFailed'));
        this.setState({ frontendUploading: false });
      }
    );
    return false;
  };

  handleUploadPc = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.msi')) {
      message.error(Lang.t('preference.invalidPcPackage'));
      return false;
    }
    if (!this.state.pcVersion.trim()) {
      message.error(Lang.t('preference.pcVersionRequired'));
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('version', this.state.pcVersion.trim());
    this.setState({ pcUploading: true });
    this.preference.httpUpload(
      Preference.URL_API_UPGRADE_PC,
      formData,
      () => {
        message.success(Lang.t('preference.upgradePcSuccess'));
        this.setState({ pcUploading: false });
      },
      (err: any) => {
        console.error('上传 PC 客户端升级包失败:', err);
        const msg = err && err.msg ? err.msg : Lang.t('preference.upgradePcFailed');
        message.error(msg);
        this.setState({ pcUploading: false });
      }
    );
    return false;
  };

  handleUploadMobile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.apk')) {
      message.error(Lang.t('preference.invalidMobilePackage'));
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);
    this.setState({ mobileUploading: true });
    this.preference.httpUpload(
      Preference.URL_API_UPGRADE_MOBILE,
      formData,
      () => {
        message.success(Lang.t('preference.upgradeMobileSuccess'));
        this.setState({ mobileUploading: false });
      },
      (err: any) => {
        console.error('上传手机客户端升级包失败:', err);
        message.error(Lang.t('preference.upgradeMobileFailed'));
        this.setState({ mobileUploading: false });
      }
    );
    return false;
  };

  // 整体升级：获取备份记录
  fetchBackupRecords = () => {
    this.setState({ backupRecordsLoading: true });
    fetch(Preference.URL_API_UPGRADE_BACKUP_LIST, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        this.setState({ backupRecordsLoading: false });
        if (result.code === 200 || result.code === 'OK') {
          this.setState({ backupRecords: result.data || [] });
        } else {
          message.error(result.message || Lang.t('preference.backupLoadFailed'));
        }
      })
      .catch(err => {
        console.error('获取备份记录失败:', err);
        message.error(Lang.t('preference.backupLoadFailed'));
        this.setState({ backupRecordsLoading: false });
      });
  };

  // 整体升级：一键备份
  handleBackupNow = () => {
    Modal.confirm({
      title: Lang.t('preference.backupConfirmTitle'),
      content: Lang.t('preference.backupConfirmContent'),
      okText: Lang.t('confirm'),
      cancelText: Lang.t('cancel'),
      onOk: () => {
        this.setState({ backupRunning: true });
        fetch(Preference.URL_API_UPGRADE_BACKUP, {
          method: 'POST',
          credentials: 'include',
        })
          .then(res => res.json())
          .then(result => {
            this.setState({ backupRunning: false });
            if (result.code === 200 || result.code === 'OK') {
              const data = result.data || {};
              this.setState({
                backupResult: {
                  backupDir: data.backupDir || '',
                  output: data.output || '',
                },
                backupResultVisible: true,
              });
              message.success(Lang.t('preference.backupSuccess'));
              this.fetchBackupRecords();
            } else {
              message.error(result.message || Lang.t('preference.backupFailed'));
            }
          })
          .catch(err => {
            console.error('一键备份失败:', err);
            this.setState({ backupRunning: false });
            message.error(Lang.t('preference.backupFailed'));
          });
      },
    });
  };

  // 完整升级：读取升级状态（用于启动后轮询与刷新页面后恢复）
  fetchUpgradeStatusOnce = () => {
    fetch(Preference.URL_API_UPGRADE_STATUS, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 200 || result.code === 'OK') {
          const st = result.data || {};
          const state = st.state || 'idle';
          const inProgress = ['starting', 'backing_up', 'uninstalling', 'installing', 'restoring'].indexOf(state) >= 0;
          if (inProgress) {
            this.setState({
              fullUpgradeState: state,
              fullUpgradeMessage: st.message || '',
              fullUpgradeStarted: true,
            });
            this.startUpgradePolling();
          } else if (state === 'done' || state === 'failed') {
            this.setState({
              fullUpgradeState: state,
              fullUpgradeMessage: st.message || '',
              fullUpgradeStarted: true,
              fullUpgradeDone: state === 'done',
            });
          }
        }
      })
      .catch(() => {
        // 服务不可达（升级重启中）时忽略
      });
  };

  // 完整升级：启动轮询
  startUpgradePolling = () => {
    this.stopUpgradePolling();
    this.setState({ fullUpgradeRunning: true, fullUpgradeStarted: true });
    this.pollUpgradeStatus();
    this.fetchUpgradeLog();
    const timer = window.setInterval(() => {
      this.pollUpgradeStatus();
      this.fetchUpgradeLog();
    }, 2500);
    this.setState({ upgradePollTimer: timer });
  };

  stopUpgradePolling = () => {
    if (this.state.upgradePollTimer) {
      window.clearInterval(this.state.upgradePollTimer);
    }
    this.setState({ upgradePollTimer: null, fullUpgradeRunning: false });
  };

  pollUpgradeStatus = () => {
    fetch(Preference.URL_API_UPGRADE_STATUS, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 200 || result.code === 'OK') {
          const st = result.data || {};
          const state = st.state || 'idle';
          this.setState({
            fullUpgradeState: state,
            fullUpgradeMessage: st.message || '',
          });
          if (state === 'done' || state === 'failed') {
            this.stopUpgradePolling();
            this.fetchUpgradeLog();
            this.setState({
              fullUpgradeDone: state === 'done',
              fullUpgradeResultVisible: true,
            });
          }
        }
      })
      .catch(() => {
        // 服务重启中，保持轮询
        this.setState({ fullUpgradeMessage: Lang.t('preference.upgradeRestarting') });
      });
  };

  fetchUpgradeLog = () => {
    fetch(Preference.URL_API_UPGRADE_LOG, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 200 || result.code === 'OK') {
          const data = result.data || {};
          if (typeof data.log === 'string') {
            this.setState({ fullUpgradeLog: data.log });
          }
        }
      })
      .catch(() => {
        // 服务重启中，忽略
      });
  };

  // 完整升级：选择安装包并确认启动
  handleStartUpgrade = (file: File) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.tar.gz') && !name.endsWith('.tgz')) {
      message.error(Lang.t('preference.fullUpgradeInvalidPackage'));
      return false;
    }
    Modal.confirm({
      title: Lang.t('preference.fullUpgradeConfirmTitle'),
      content: Lang.t('preference.fullUpgradeConfirmContent'),
      okText: Lang.t('confirm'),
      cancelText: Lang.t('cancel'),
      onOk: () => {
        this.doStartUpgrade(file);
      },
    });
    return false;
  };

  doStartUpgrade = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const restoreDir = (this.state.fullUpgradeRestoreDir || '').trim();
    if (restoreDir) {
      formData.append('restoreDir', restoreDir);
    }
    this.setState({ fullUpgradeUploading: true });
    fetch(Preference.URL_API_UPGRADE_FULL, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then(res => res.json())
      .then(result => {
        this.setState({ fullUpgradeUploading: false });
        if (result.code === 200 || result.code === 'OK') {
          message.success(Lang.t('preference.fullUpgradeStarted'));
          this.startUpgradePolling();
        } else {
          message.error(result.message || Lang.t('preference.fullUpgradeStartFailed'));
          this.setState({
            fullUpgradeState: 'failed',
            fullUpgradeMessage: result.message || Lang.t('preference.upgradeStateFailed'),
            fullUpgradeStarted: true,
          });
        }
      })
      .catch(err => {
        console.error('启动整体升级失败:', err);
        this.setState({ fullUpgradeUploading: false });
        message.error(Lang.t('preference.fullUpgradeStartFailed'));
      });
  };

  // 整体升级：页面区块
  renderOverallUpgrade() {
    const {
      backupRunning,
      backupRecords,
      backupRecordsLoading,
      backupResultVisible,
      backupResult,
      fullUpgradeUploading,
      fullUpgradeStarted,
      fullUpgradeState,
      fullUpgradeMessage,
      fullUpgradeLog,
      fullUpgradeRestoreDir,
      fullUpgradeResultVisible,
      fullUpgradeDone,
    } = this.state;

    const stepIndexMap: { [key: string]: number } = {
      starting: 0,
      backing_up: 0,
      uninstalling: 1,
      installing: 2,
      restoring: 3,
      done: 4,
    };
    const stepIndex = stepIndexMap[fullUpgradeState] !== undefined ? stepIndexMap[fullUpgradeState] : 0;
    const stepStatus = fullUpgradeState === 'failed' ? 'error' : fullUpgradeState === 'done' ? 'finish' : 'process';

    const revisitUrl = `http://${window.location.hostname}:8010`;
    return (
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>{Lang.t('preference.overallUpgrade')}</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {/* 完整升级：上传安装包并自动执行 */}
        <Alert
          message={Lang.t('preference.fullUpgradeStart')}
          description={Lang.t('preference.fullUpgradeStartDesc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={8}>
            <Input
              placeholder={Lang.t('preference.fullUpgradeRestoreDirTip')}
              prefix={<SyncOutlined />}
              value={fullUpgradeRestoreDir}
              onChange={e => this.setState({ fullUpgradeRestoreDir: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={16}>
            <Upload
              accept=".tar.gz,.tgz"
              showUploadList={false}
              beforeUpload={this.handleStartUpgrade}
              disabled={fullUpgradeStarted && fullUpgradeState !== 'done' && fullUpgradeState !== 'failed'}
            >
              <Button
                type="primary"
                danger
                icon={<ThunderboltOutlined />}
                loading={fullUpgradeUploading}
                disabled={fullUpgradeStarted && fullUpgradeState !== 'done' && fullUpgradeState !== 'failed'}
              >
                {fullUpgradeUploading
                  ? Lang.t('preference.fullUpgradeUploading')
                  : Lang.t('preference.fullUpgradeStart')}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              {Lang.t('preference.fullUpgradeRestoreDir')}
            </div>
          </Col>
        </Row>

        {/* 升级进度 */}
        {fullUpgradeStarted && (
          <div style={{ margin: '16px 0 24px' }}>
            <Divider orientation="left">{Lang.t('preference.fullUpgradeProgress')}</Divider>
            <Steps size="small" current={stepIndex} status={stepStatus as any}>
              <Steps.Step title={Lang.t('preference.overallStepBackup')} />
              <Steps.Step title={Lang.t('preference.overallStepUninstall')} />
              <Steps.Step title={Lang.t('preference.overallStepInstall')} />
              <Steps.Step title={Lang.t('preference.overallStepRestore')} />
            </Steps>
            <div
              style={{
                marginTop: 12,
                color: fullUpgradeState === 'failed' ? '#ff4d4f' : '#666',
                fontSize: 13,
              }}
            >
              {fullUpgradeMessage || Lang.t('preference.fullUpgradeRunning')}
            </div>
            {fullUpgradeLog && (
              <pre
                style={{
                  maxHeight: 240,
                  overflow: 'auto',
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  marginTop: 12,
                }}
              >
                {fullUpgradeLog}
              </pre>
            )}
          </div>
        )}

        <Divider orientation="left">{Lang.t('preference.backupNow')}</Divider>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={backupRunning}
          onClick={this.handleBackupNow}
        >
          {backupRunning ? Lang.t('preference.backupLoading') : Lang.t('preference.backupNow')}
        </Button>

        <Divider orientation="left">{Lang.t('preference.backupRecords')}</Divider>
        <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
          <Col span={24}>
            <Button
              size="small"
              icon={<SyncOutlined />}
              loading={backupRecordsLoading}
              onClick={this.fetchBackupRecords}
            >
              {Lang.t('preference.backupRefresh')}
            </Button>
          </Col>
        </Row>
        <List
          size="small"
          loading={backupRecordsLoading}
          dataSource={backupRecords}
          locale={{ emptyText: Lang.t('preference.backupEmpty') }}
          renderItem={item => (
            <List.Item>
              <Space direction="vertical" size={0}>
                <span style={{ fontWeight: 500 }}>{item.name}</span>
                <span style={{ fontSize: 12, color: '#999' }}>
                  {Lang.t('preference.backupDir')}: {item.path}（{item.size}）{item.mtime}
                </span>
              </Space>
            </List.Item>
          )}
        />

        <Modal
          title={Lang.t('preference.backupSuccess')}
          visible={backupResultVisible}
          footer={
            <Button type="primary" onClick={() => this.setState({ backupResultVisible: false })}>
              {Lang.t('preference.backupClose')}
            </Button>
          }
          onCancel={() => this.setState({ backupResultVisible: false })}
          width={720}
        >
          {backupResult && (
            <div>
              <Alert
                type="success"
                showIcon
                message={`${Lang.t('preference.backupDir')}: ${backupResult.backupDir}`}
                style={{ marginBottom: 12 }}
              />
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                {Lang.t('preference.backupViewLog')}
              </div>
              <pre
                style={{
                  maxHeight: 320,
                  overflow: 'auto',
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {backupResult.output}
              </pre>
            </div>
          )}
        </Modal>

        <Modal
          title={
            fullUpgradeDone
              ? Lang.t('preference.upgradeStateDone')
              : Lang.t('preference.upgradeStateFailed')
          }
          visible={fullUpgradeResultVisible}
          onOk={() => {
            this.setState({ fullUpgradeResultVisible: false });
            if (fullUpgradeDone) {
              window.location.href = revisitUrl;
            }
          }}
          okText={fullUpgradeDone ? Lang.t('preference.visitAgain') : Lang.t('confirm')}
          onCancel={() => this.setState({ fullUpgradeResultVisible: false })}
          width={720}
        >
          {fullUpgradeDone ? (
            <Alert
              type="success"
              showIcon
              message={Lang.t('preference.upgradeDoneContent', revisitUrl)}
              style={{ marginBottom: 12 }}
            />
          ) : (
            <Alert
              type="error"
              showIcon
              message={Lang.t('preference.upgradeFailedContent')}
              style={{ marginBottom: 12 }}
            />
          )}
          {fullUpgradeLog && (
            <pre
              style={{
                maxHeight: 320,
                overflow: 'auto',
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {fullUpgradeLog}
            </pre>
          )}
        </Modal>
      </Card>
    );
  }

  // 新增：系统升级页面
  renderUpgradeSettings() {
    const { frontendUploading, pcUploading, pcVersion, mobileUploading } = this.state;
    return (
      <Row gutter={[24, 24]}>
        <Col span={24}>
          {this.renderOverallUpgrade()}
          <Card
            title={
              <Space>
                <UploadOutlined />
                <span>{Lang.t('preference.systemUpgrade')}</span>
              </Space>
            }
          >
            <Alert
              message={Lang.t('preference.systemUpgradeDesc')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            {/* 前端升级 */}
            <Divider orientation="left">{Lang.t('preference.frontendUpgrade')}</Divider>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={6} md={4}>
                <span>{Lang.t('preference.frontendUpgrade')}：</span>
              </Col>
              <Col xs={24} sm={18} md={16}>
                <Upload
                  accept=".zip"
                  showUploadList={false}
                  beforeUpload={this.handleUploadFrontend}
                >
                  <Button type="primary" icon={<UploadOutlined />} loading={frontendUploading}>
                    {Lang.t('preference.choosePackage')}
                  </Button>
                </Upload>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {Lang.t('preference.frontendUpgradeTip')}
                </div>
              </Col>
            </Row>

            {/* PC 客户端升级 */}
            <Divider orientation="left">{Lang.t('preference.pcUpgrade')}</Divider>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={6} md={4}>
                <span>{Lang.t('preference.pcUpgrade')}：</span>
              </Col>
              <Col xs={24} sm={18} md={16}>
                <Space>
                  <Upload
                    accept=".msi"
                    showUploadList={false}
                    beforeUpload={this.handleUploadPc}
                  >
                    <Button type="primary" icon={<UploadOutlined />} loading={pcUploading}>
                      {Lang.t('preference.choosePackage')}
                    </Button>
                  </Upload>
                  <Input
                    placeholder={Lang.t('preference.pcVersionPlaceholder')}
                    style={{ width: 220 }}
                    value={pcVersion}
                    onChange={(e) => this.setState({ pcVersion: e.target.value })}
                  />
                </Space>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {Lang.t('preference.pcUpgradeTip')}
                </div>
              </Col>
            </Row>

            {/* 手机客户端升级 */}
            <Divider orientation="left">{Lang.t('preference.mobileUpgrade')}</Divider>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={6} md={4}>
                <span>{Lang.t('preference.mobileUpgrade')}：</span>
              </Col>
              <Col xs={24} sm={18} md={16}>
                <Upload
                  accept=".apk"
                  showUploadList={false}
                  beforeUpload={this.handleUploadMobile}
                >
                  <Button type="primary" icon={<UploadOutlined />} loading={mobileUploading}>
                    {Lang.t('preference.choosePackage')}
                  </Button>
                </Upload>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {Lang.t('preference.mobileUpgradeTip')}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  }

  render() {
    const { ctrlMod, syncSet } = this.preference;
    return (
      <div className="page-preference-index">
        <HwzTitle name={Lang.t('layout.setting')}>
          <Button className={'ml10'} type={'primary'} danger={true} onClick={this.cleanup.bind(this)} icon={<ThunderboltOutlined />}>
            {Lang.t('preference.systemCleanup')}
          </Button>
        </HwzTitle>
        <HwzContentCard>
          <Tabs defaultActiveKey="basic" type="card">
            <TabPane tab={Lang.t('preference.basicSettings')} key="basic">
              {this.renderBasicSettings()}
            </TabPane>
            <TabPane tab={Lang.t('preference.advancedSettings')} key="advanced">
              {this.renderAdvancedSettings()}
            </TabPane>
            {/* 新增级联设置标签页 */}
            <TabPane tab={Lang.t('preference.cascadeSettings')} key="cascade">
              {this.renderCascadeSettings()}
            </TabPane>
            {/* 新增系统升级标签页 */}
            <TabPane tab={Lang.t('preference.systemUpgrade')} key="upgrade">
              {this.renderUpgradeSettings()}
            </TabPane>
          </Tabs>
        </HwzContentCard>
      </div>
    );
  }
}